import React, { useEffect, useState } from 'react';
import './css/Login.css';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import { getFavourationList, userLogin } from '../features/auth/authAction'
import { userProfile, fetchUserSpecificData, fetchUserPermission } from '../features/auth/authAction';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import { Box, Container, Typography, Button, Card, CardContent, useTheme } from '@mui/material';
import Alert from '../helpers/alertBox'
import ErrorBoundary from '../helpers/errorBound'


const labels = {
  si: {
    title: 'ඇතුල් වන්න',
    subtitle: 'වලංගු විස්තර ලබා දී අපගේ සේවාව භුක්ති විඳින්න',
    email: 'ඊමේල් හෝ දුරකථන අංකය',
    password: 'මුරපදය',
    submit: 'ඇතුල් වන්න',
  },
  en: {
    title: 'Log In',
    subtitle: 'Enter valid details to enjoy our service',
    email: 'Email or Phone Number',
    password: 'Password',
    submit: 'Log In',
  }
};

const Login = ({ language }) => {
  const { loading, loggedUserInfo, error, loginSuccess } = useSelector((state) => state.auth)
  const userSpecificDetails = useSelector(state => state.user?.userSpecificData);

  // Clean, prop-driven language selection
  const t = labels[language] ?? labels.si;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { register, handleSubmit } = useForm();
  const [showPassword, setShowPassword] = React.useState(false);
  const theme = useTheme();

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };
  const profileInfo = useSelector((state) => state.auth?.profileInfo?.user);
  const userLoading = useSelector((state) => state.user?.loading);
  const [hasNavigated, setHasNavigated] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);

  useEffect(() => {
    if (loggedUserInfo?.user_id && !dataFetched) {
      setDataFetched(true);
      dispatch(userProfile());
      dispatch(fetchUserSpecificData(loggedUserInfo.user_id));
      dispatch(fetchUserPermission(loggedUserInfo.user_id));
      dispatch(getFavourationList(loggedUserInfo.user_id));
    }
  }, [loggedUserInfo, dispatch, dataFetched]);

  useEffect(() => {
    const userId = loggedUserInfo?.user_id;

    // Only navigate if we have a userId, haven't navigated yet, data is loaded, and we've fetched data
    if (userId && !hasNavigated && !userLoading && dataFetched) {
      // Check if user has a profile (userSpecificDetails exists and has userData array with items)
      // userSpecificDetails can be: null (not loaded), { userData: [] } (no profile), or { userData: [...] } (has profile)
      const hasProfile = userSpecificDetails?.userData && Array.isArray(userSpecificDetails.userData) && userSpecificDetails.userData.length > 0;

      // Check if fetchUserSpecificData has completed (not null, not undefined)
      // When fetchUserSpecificData completes, it will be an object (even if empty)
      const dataLoaded = userSpecificDetails !== null && userSpecificDetails !== undefined;

      if (dataLoaded) {
        setHasNavigated(true);
        if (hasProfile) {
          // User has profile, navigate to profile page
          navigate(`/moreDetailsPage/${userId}/${userId}`, { replace: true });
        } else {
          // User doesn't have profile (empty array or no userData), navigate to create profile page
          navigate(`/service/${userId}`, { replace: true });
        }
      }
    }
  }, [loggedUserInfo, userSpecificDetails, userLoading, hasNavigated, dataFetched, navigate, dispatch]);

  // Reset navigation flag when user logs out or component unmounts
  useEffect(() => {
    if (!loggedUserInfo) {
      setHasNavigated(false);
      setDataFetched(false);
    }
  }, [loggedUserInfo]);


  const submitLogin = (data) => {
    dispatch(userLogin(data))
    if (data.email === 'rd@gmail.com' && data.password === '2025@!Kottawa') {
      navigate('/admin-permission2025');
    }
  }
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Use theme background or custom overlay if needed. 
        // For now using the faint gradient but dependent on theme could be nice.
        // Let's us theme.palette.background.default which is already set globally, 
        // but we can add a subtle tint.
        background: theme.palette.mode === 'light'
          ? 'linear-gradient(135deg, rgba(5, 74, 73, 0.05) 0%, rgba(212, 175, 55, 0.05) 100%)' // Emerald/Gold Tint
          : '#121212',
        py: { xs: 4, md: 6 },
        px: { xs: 2, sm: 3 },
      }}
    >
      <Container maxWidth="sm">
        <ErrorBoundary fallback={<p>Something went wrong</p>}>
          <Alert />
        </ErrorBoundary>
        <Card
          sx={{
            borderRadius: 4,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            border: theme.palette.custom.sectionBorder,
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: theme.palette.custom.secondaryGradient,
            },
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  mb: 1.5,
                  background: theme.palette.custom.mainGradient,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' },
                }}
              >
                {t.title}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                {t.subtitle}
              </Typography>
            </Box>

            <form onSubmit={handleSubmit(submitLogin)}>
              <FormControl fullWidth sx={{ mb: 2.5 }} variant="outlined">
                <InputLabel>{t.email}</InputLabel>
                <OutlinedInput
                  label={t.email}
                  {...register('email')}
                  sx={{
                    borderRadius: 2,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  }}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mb: 3 }} variant="outlined">
                <InputLabel htmlFor="outlined-adornment-password">{t.password}</InputLabel>
                <OutlinedInput
                  id="outlined-adornment-password"
                  type={showPassword ? 'text' : 'password'}
                  label={t.password}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  {...register('password')}
                  sx={{
                    borderRadius: 2,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  }}
                />
              </FormControl>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                disabled={loading}
                sx={{
                  py: 1.5,
                  borderRadius: 999,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  background: theme.palette.custom.mainGradient,
                  boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                  '&:hover': {
                    background: theme.palette.custom.mainGradient,
                    opacity: 0.9,
                    boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {t.submit}
              </Button>
            </form>
          </CardContent>
        </Card>
      </Container>
    </Box>
  )
}
export default Login;